import { PROVIDERS, api } from '@agency/shared';
import { Button, Chip, DataTable, Dialog, Field, MetricCell, MetricRow, PageHeader, Select, Skeleton, StatusDot, fmtCount, fmtTimestamp, type DataTableColumn } from '@agency/ui';
import { useNavigate } from '@tanstack/react-router';
import { Download } from 'lucide-react';
import { useMemo, useState } from 'react';
import { AccountLink, ErrorCallout, FilterInput, FilterSelect, Grid, IdCell, LoadMore, Page, Stamp, TableCard, UserLink } from '../../app/widgets.js';
import { API_BASE } from '../../lib/api.js';
import { OUTCOME_TONE, TRUST_LABEL, TRUST_TONE } from '../../lib/labels.js';
import { useMutation } from '../../lib/mutate.js';
import { useSession } from '../../lib/session.js';
import { useApi, usePaged } from '../../lib/useApi.js';

const RANGES = [
  { value: '1h', label: 'Last hour', ms: 3_600_000 },
  { value: '24h', label: 'Last 24 hours', ms: 86_400_000 },
  { value: '7d', label: 'Last 7 days', ms: 7 * 86_400_000 },
  { value: '30d', label: 'Last 30 days', ms: 30 * 86_400_000 },
  { value: 'all', label: 'All time', ms: 0 },
];

export type AuditFilters = Omit<api.AuditQuery, 'cursor' | 'limit' | 'order'> & { order?: 'asc' | 'desc' };

export function useAuditFilters() {
  const [range, setRange] = useState('7d');
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [accountId, setAccountId] = useState<string | undefined>(undefined);
  const [provider, setProvider] = useState<string | undefined>(undefined);
  const [deviceId, setDeviceId] = useState('');
  const [family, setFamily] = useState<string | undefined>(undefined);
  const [action, setAction] = useState<string | undefined>(undefined);
  const [appVersion, setAppVersion] = useState('');
  const [outcome, setOutcome] = useState<string | undefined>(undefined);
  const [operationId, setOperationId] = useState('');
  const [trustLevel, setTrustLevel] = useState<string | undefined>(undefined);
  const from = useMemo(() => {
    const r = RANGES.find((x) => x.value === range);
    return r && r.ms > 0 ? new Date(Date.now() - r.ms).toISOString() : undefined;
  }, [range]);
  const filters = useMemo<AuditFilters>(
    () => ({
      ...(from ? { from } : {}),
      ...(userId ? { userId: userId as api.User['id'] } : {}),
      ...(accountId ? { accountId: accountId as api.Account['id'] } : {}),
      ...(provider ? { provider: provider as 'vanta' | 'drata' } : {}),
      ...(deviceId ? { deviceId: deviceId as api.Device['id'] } : {}),
      ...(family ? { family: family as api.AuditActionFamily } : {}),
      ...(action ? { action: action as api.AuditAction } : {}),
      ...(appVersion ? { appVersion } : {}),
      ...(outcome ? { outcome: outcome as api.AuditOutcome } : {}),
      ...(operationId ? { operationId: operationId as api.AuditEvent['operationId'] } : {}),
      ...(trustLevel ? { trustLevel: trustLevel as api.AuditTrustLevel } : {}),
    }),
    [from, userId, accountId, provider, deviceId, family, action, appVersion, outcome, operationId, trustLevel],
  );
  return { filters, state: { range, userId, accountId, provider, deviceId, family, action, appVersion, outcome, operationId, trustLevel }, set: { setRange, setUserId, setAccountId, setProvider, setDeviceId, setFamily, setAction, setAppVersion, setOutcome, setOperationId, setTrustLevel } };
}

export function AuditFilterBar({ f }: { f: ReturnType<typeof useAuditFilters> }) {
  const session = useSession();
  const people = useApi('admin.people.list', { input: { limit: 200 } }, { enabled: session.has(['people.manage', 'audit.read', 'account.assign']) });
  const accounts = useApi('admin.accounts.list', { input: { limit: 200 } }, { enabled: session.has(['account.assign', 'account.register', 'audit.read']) });
  const families = Object.keys(api.AUDIT_ACTION_FAMILIES) as api.AuditActionFamily[];
  const actions = f.state.family ? api.AUDIT_ACTION_FAMILIES[f.state.family as api.AuditActionFamily] : api.AUDIT_ACTIONS;
  return (
    <div className="flex flex-wrap items-end gap-3">
      <FilterSelect label="Time" value={f.state.range} onChange={(v) => f.set.setRange(v ?? 'all')} options={RANGES.map((r) => ({ value: r.value, label: r.label }))} allLabel="All time" width={150} />
      <FilterSelect label="User" value={f.state.userId} onChange={f.set.setUserId} options={(people.data?.items ?? []).map((u) => ({ value: u.id, label: u.displayName }))} width={170} />
      <FilterSelect label="Account" value={f.state.accountId} onChange={f.set.setAccountId} options={(accounts.data?.items ?? []).map((a) => ({ value: a.id, label: a.displayName }))} width={170} />
      <FilterSelect label="Provider" value={f.state.provider} onChange={f.set.setProvider} options={PROVIDERS.map((p) => ({ value: p, label: p }))} width={110} />
      <FilterSelect label="Family" value={f.state.family} onChange={(v) => { f.set.setFamily(v); f.set.setAction(undefined); }} options={families.map((x) => ({ value: x, label: x }))} width={130} />
      <FilterSelect label="Action" value={f.state.action} onChange={f.set.setAction} options={[...actions].map((a) => ({ value: a, label: a }))} width={220} />
      <FilterSelect label="Outcome" value={f.state.outcome} onChange={f.set.setOutcome} options={api.AUDIT_OUTCOMES.map((o) => ({ value: o, label: o }))} width={120} />
      <FilterSelect label="Trust level" value={f.state.trustLevel} onChange={f.set.setTrustLevel} options={api.AUDIT_TRUST_LEVELS.map((t) => ({ value: t, label: TRUST_LABEL[t] }))} width={170} />
      <FilterInput label="Device id" value={f.state.deviceId} onChange={f.set.setDeviceId} placeholder="uuid" mono width={200} />
      <FilterInput label="App version" value={f.state.appVersion} onChange={f.set.setAppVersion} placeholder="1.0.0" mono width={110} />
      <FilterInput label="Correlation id" value={f.state.operationId} onChange={f.set.setOperationId} placeholder="operation uuid" mono width={220} />
    </div>
  );
}

export function AuditExplorerPage() {
  const session = useSession();
  const navigate = useNavigate();
  const f = useAuditFilters();
  const [exportOpen, setExportOpen] = useState(false);
  const events = usePaged('admin.audit.query', { input: { ...f.filters, limit: 100 } });
  const summary = useApi('admin.audit.summary', { input: f.filters.from ? { from: f.filters.from } : {} });
  const delivery = useApi('admin.audit.delivery', {}, { refreshMs: 60_000 });
  const canExport = session.has('audit.export');

  const columns = useMemo<DataTableColumn<api.AuditEvent>[]>(
    () => [
      { id: 'received', header: 'Received', cell: ({ row }) => <Stamp value={row.original.receivedAt} />, size: 135 },
      { id: 'occurred', header: 'Occurred', cell: ({ row }) => <Stamp value={row.original.occurredAt} />, size: 135 },
      { id: 'actor', header: 'Actor', cell: ({ row }) => <UserLink userId={row.original.actorUserId} />, size: 130 },
      { id: 'device', header: 'Device', cell: ({ row }) => <IdCell value={row.original.deviceId} />, size: 95 },
      { id: 'account', header: 'Account', cell: ({ row }) => <AccountLink accountId={row.original.accountId} />, size: 130 },
      { id: 'action', header: 'Action', cell: ({ row }) => <span className="font-mono text-xs-plus text-ink">{row.original.action}</span>, size: 200 },
      { id: 'outcome', header: 'Outcome', cell: ({ row }) => <Chip tone={OUTCOME_TONE[row.original.outcome]}>{row.original.outcome}</Chip>, size: 90 },
      { id: 'operation', header: 'Operation id', cell: ({ row }) => <IdCell value={row.original.operationId} />, size: 100 },
      { id: 'sequence', header: 'Seq', cell: ({ row }) => <span className="block text-right font-mono tabular-nums text-mute">{row.original.sequence ?? '--'}</span>, size: 50 },
      { id: 'trust', header: 'Trust', cell: ({ row }) => (row.original.trustLevel ? <Chip tone={TRUST_TONE[row.original.trustLevel]}>{TRUST_LABEL[row.original.trustLevel]}</Chip> : <span className="text-mute">--</span>), size: 140 },
      { id: 'app', header: 'App', cell: ({ row }) => <span className="font-mono text-xs-plus text-mute">{row.original.appVersion}</span>, size: 70 },
    ],
    [],
  );

  const s = summary.data;
  const d = delivery.data;
  return (
    <Page>
      <PageHeader
        eyebrow="Audit"
        eyebrowPath="Audit explorer"
        title="Audit explorer"
        subtitle={s ? `${fmtCount(s.total)} events in range · ${fmtCount(s.deniedActions)} denied · last received ${s.lastReceivedAt ? fmtTimestamp(s.lastReceivedAt) : '--'} · ${d ? `${d.reporters.length} reporters, ${d.reporters.filter((r) => r.stale).length} stale` : '…'}` : 'Loading summary.'}
        actions={<Button variant="primary" icon={Download} disabled={!canExport} onClick={() => setExportOpen(true)}>Export</Button>}
      />
      <ErrorCallout error={events.error ?? summary.error} onRetry={() => void events.reload()} className="mt-6" />
      <Grid>
        <div className="col-span-12 lg:col-span-8">
          {s ? (
            <MetricRow columns={4}>
              <MetricCell label="Events" value={fmtCount(s.total)} />
              <MetricCell label="Success" value={fmtCount(s.byOutcome.success ?? 0)} tone="ok" />
              <MetricCell label="Denied" value={fmtCount(s.byOutcome.denied ?? 0)} tone={(s.byOutcome.denied ?? 0) > 0 ? 'brand' : 'ink'} />
              <MetricCell label="Failed" value={fmtCount(s.byOutcome.failed ?? 0)} tone={(s.byOutcome.failed ?? 0) > 0 ? 'warn' : 'ink'} />
            </MetricRow>
          ) : (
            <Skeleton height={56} />
          )}
          {s ? (
            <div className="mt-3 flex flex-wrap gap-1">
              {Object.entries(s.byFamily).sort((a, b) => b[1] - a[1]).map(([fam, n]) => (
                <button key={fam} type="button" onClick={() => f.set.setFamily(f.state.family === fam ? undefined : fam)} className={`inline-flex items-center gap-2 rounded-sm border px-2 py-0.5 font-mono text-2xs uppercase tracking-wider ${f.state.family === fam ? 'border-brand text-ink' : 'border-line text-mute hover:text-ink'}`}>
                  {fam} <span className="tabular-nums text-dim">{fmtCount(n)}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>
        <div className="col-span-12 lg:col-span-4 rounded-sm border border-line bg-panel">
          <div className="flex items-center justify-between border-b border-hair px-5 py-3">
            <h3 className="text-[13px] font-medium tracking-tight">Delivery</h3>
            <Button variant="ghost" onClick={() => void navigate({ to: '/exports' })}>Delivery panel</Button>
          </div>
          <div className="px-5 py-3 text-[12.5px]">
            {!d ? (
              <Skeleton height={60} />
            ) : d.reporters.length === 0 ? (
              <p className="text-mute">No device has reported audit batches yet. An offline device is not evidence that no work occurred.</p>
            ) : (
              <ul className="divide-y divide-hair">
                {d.reporters.slice(0, 5).map((r) => (
                  <li key={r.deviceId} className="flex items-center justify-between py-1.5">
                    <StatusDot tone={r.stale ? 'warn' : 'ok'} mono={false} label={<span>{r.deviceName} <span className="text-mute">· {r.userDisplayName}</span></span>} />
                    <span className="font-mono text-2xs text-mute">{r.lastReceivedAt ? fmtTimestamp(r.lastReceivedAt) : '--'} · {r.unacknowledged} unack · {r.gaps.length} gaps</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <TableCard title="Events" subtitle={`${events.items.length} shown · newest first`} footer={<LoadMore hasMore={events.hasMore} loading={events.loadingMore} onLoadMore={() => void events.loadMore()} />}>
          <div className="border-b border-hair px-5 py-3"><AuditFilterBar f={f} /></div>
          <DataTable<api.AuditEvent> aria-label="Audit events" columns={columns} rows={events.items} rowKey={(r) => r.eventId} height={Math.min(760, Math.max(240, events.items.length * 34 + 40))} state={events.loading ? 'loading' : events.items.length === 0 ? 'empty' : 'complete'} emptyMessage="No events match these filters." noun="events" onSelect={(id) => void navigate({ to: '/audit/$eventId', params: { eventId: id } })} onActivate={(id) => void navigate({ to: '/audit/$eventId', params: { eventId: id } })} isCritical={(r) => r.outcome === 'denied'} />
        </TableCard>
      </Grid>
      <AuditExportDialog open={exportOpen} onOpenChange={setExportOpen} filters={f.filters} />
    </Page>
  );
}

export function AuditExportDialog({ open, onOpenChange, filters }: { open: boolean; onOpenChange: (o: boolean) => void; filters: AuditFilters }) {
  const mutation = useMutation();
  const [format, setFormat] = useState<'csv' | 'json'>('csv');
  const zones = ['UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'Europe/London', 'Europe/Berlin'];
  const local = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const [timezone, setTimezone] = useState(local);
  const [result, setResult] = useState<api.ExportResult | null>(null);
  const active = Object.entries(filters).filter(([, v]) => v !== undefined);
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setResult(null); }} title="Export audit events" subtitle="Requires audit.export · records audit.exported" width={500} actions={result ? <Button variant="primary" icon={Download} onClick={() => window.open(`${API_BASE}${result.downloadPath}`, '_blank', 'noopener')}>Download {result.format.toUpperCase()}</Button> : <><Button variant="tertiary" onClick={() => onOpenChange(false)}>Cancel</Button><Button variant="primary" loading={mutation.busy} onClick={async () => { const r = await mutation.run('admin.audit.export', { input: { format, filters: { ...filters, order: 'desc' } as api.AuditExportRequest['filters'], timezone } }, { title: (d) => `Export ready · ${d.rowCount} rows`, invalidate: ['admin.audit'] }); if (r) setResult(r); }}>Generate</Button></>}>
      {result ? (
        <div className="text-[13px] text-ink/85">
          <p>Generated {fmtTimestamp(result.generatedAt)} · {result.rowCount} rows · {result.timezone}.</p>
          <p className="mt-2 text-xs text-mute">Download expires {fmtTimestamp(result.expiresAt)} and is available to you only. Export id <span className="font-mono">{result.exportId}</span>.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <Field label="Format"><Select value={format} onValueChange={(v) => setFormat(v as 'csv' | 'json')} options={[{ value: 'csv', label: 'CSV' }, { value: 'json', label: 'JSON' }]} /></Field>
          <Field label="Timezone" help="Applied to exported timestamps. Generated-at is recorded in UTC."><Select value={timezone} onValueChange={setTimezone} options={(zones.includes(local) ? zones : [local, ...zones]).map((z) => ({ value: z, label: z }))} /></Field>
          <div>
            <p className="label-mono">Filters</p>
            {active.length === 0 ? <p className="mt-1 text-xs text-mute">No filters; every event is exported.</p> : (
              <ul className="mt-1 flex flex-wrap gap-1">{active.map(([k, v]) => <li key={k}><Chip tone="mute"><span className="normal-case">{k}: {String(v).slice(0, 24)}</span></Chip></li>)}</ul>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
}
